import React, { useEffect, useState, useRef, useCallback } from 'react';
import type { Chart as ChartJS } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchInventoryComparisonData, InventoryComparisonResult } from '../../services/inventoryComparisonService';
import { GHG_INVENTORY_CHART_HEIGHT_PX } from '../../theme/ghgInventoryChartLayout';
import { downloadChartJsInstanceAsPNG, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { flattenInventoryComparison } from '../../services/ghgInventoryDownloadService';

interface InventoryComparisonChartProps {
  location: string;
  onDownloadPNGReady?: (handler: () => void) => void;
  onDownloadCSVReady?: (handler: () => void) => void;
}

export const InventoryComparisonChart: React.FC<InventoryComparisonChartProps> = ({
  location,
  onDownloadPNGReady,
  onDownloadCSVReady,
}) => {
  const [comparisonData, setComparisonData] = useState<InventoryComparisonResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<ChartJS<'line'>>(null);

  const loadData = useCallback(async () => {
    if (!location) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const result = await fetchInventoryComparisonData(location);
      setComparisonData(result);
    } catch (err) {
      console.error('Error loading inventory comparison data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load inventory comparison data');
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleDownloadPNG = useCallback(() => {
    downloadChartJsInstanceAsPNG(
      chartRef.current,
      formatFilename(location, 'GHG_Inventory_Comparison')
    );
  }, [location]);

  const handleDownloadCSV = useCallback(() => {
    if (!comparisonData) return;
    downloadDataAsCSV(
      flattenInventoryComparison(location, comparisonData),
      formatFilename(location, 'GHG_Inventory_Comparison')
    );
  }, [location, comparisonData]);

  useEffect(() => {
    onDownloadPNGReady?.(handleDownloadPNG);
  }, [onDownloadPNGReady, handleDownloadPNG]);

  useEffect(() => {
    onDownloadCSVReady?.(handleDownloadCSV);
  }, [onDownloadCSVReady, handleDownloadCSV]);


  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: GHG_INVENTORY_CHART_HEIGHT_PX,
        minHeight: GHG_INVENTORY_CHART_HEIGHT_PX,
        fontSize: '16px',
        color: '#6b7280'
      }}>
        Loading inventory comparison data...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: GHG_INVENTORY_CHART_HEIGHT_PX,
        minHeight: GHG_INVENTORY_CHART_HEIGHT_PX,
        fontSize: '16px',
        color: '#ef4444'
      }}>
        Error: {error}
      </div>
    );
  }

  if (!comparisonData) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: GHG_INVENTORY_CHART_HEIGHT_PX,
        minHeight: GHG_INVENTORY_CHART_HEIGHT_PX,
        fontSize: '16px',
        color: '#6b7280'
      }}>
        No inventory comparison data available for {location}
      </div>
    );
  }

  return (
    <div style={{ 
      width: '100%', 
      height: GHG_INVENTORY_CHART_HEIGHT_PX,
      minHeight: GHG_INVENTORY_CHART_HEIGHT_PX,
      position: 'relative',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Line
        ref={chartRef}
        data={comparisonData.chartData}
        options={{
          ...comparisonData.chartOptions,
          plugins: {
            ...(comparisonData.chartOptions.plugins ?? {}),
            title: {
              display: true,
              text: `GHG inventory comparison (sector vs consumption) - ${location}`,
              font: { size: 14, weight: 600 },
            },
          },
        }}
      />
    </div>
  );
};
